import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, DollarSign, MapPin, Send, FileText, BarChart3, Briefcase, ChevronDown, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';

const Logo = () => (
  <div className="relative">
    {/* Creative stacked logo design */}
    <div className="relative w-12 h-12">
      {/* Background hexagonal shape */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 rounded-2xl shadow-lg transform rotate-6 group-hover:rotate-12 transition-transform duration-500"></div>
      {/* Foreground shape with letter mark */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-green-700 to-emerald-800 rounded-2xl shadow-xl flex items-center justify-center -rotate-3 group-hover:rotate-0 transition-all duration-500">
        {/* Custom F+S monogram with rising bars */}
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
          {/* Rising bars representing growth */}
          <rect x="3" y="14" width="3" height="7" rx="0.5" fill="white" opacity="0.5"/>
          <rect x="7" y="10" width="3" height="11" rx="0.5" fill="white" opacity="0.7"/>
          <rect x="11" y="6" width="3" height="15" rx="0.5" fill="white" opacity="0.9"/>
          <rect x="15" y="3" width="3" height="18" rx="0.5" fill="white"/>
          {/* Arrow at top */}
          <path d="M19 6L21 4M21 4L19 2M21 4H17" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {/* Floating dollar sign accent */}
      <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-amber-300 to-amber-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        <DollarSign className="w-3 h-3 text-white" strokeWidth={3} />
      </div>
    </div>
  </div>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(null);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'For Startups', path: '/for-startups', hasMega: 'startups' },
    { name: 'For Investors', path: '/for-investors', hasMega: 'investors' },
    { name: 'Industries', path: '/industries' },
    { name: 'About', path: '/about' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' }
  ];

  const startupServices = [
    { icon: DollarSign, title: 'Fundraising Assistance', description: 'End-to-end fundraising support', color: 'from-amber-400 to-orange-500', path: '/services/fundraising-assistance' },
    { icon: MapPin, title: 'Investor Discovery & Mapping', description: 'Find your kind of investor', color: 'from-emerald-400 to-green-600', path: '/services/investor-discovery' },
    { icon: Send, title: 'Investor Outreach', description: 'Direct & indirect networks', color: 'from-blue-400 to-cyan-500', path: '/services/investor-outreach' },
    { icon: FileText, title: 'Data Room Creation', description: 'Investor-ready documentation', color: 'from-rose-400 to-red-500', path: '/services/data-room' },
    { icon: BarChart3, title: 'Pitch Deck Creation', description: 'Stories that resonate', color: 'from-purple-400 to-violet-500', path: '/services/pitch-deck' },
    { icon: Briefcase, title: 'Financial Modeling', description: 'Numbers that make sense', color: 'from-teal-400 to-emerald-500', path: '/services/financial-modeling' }
  ];

  const investorServices = [
    { icon: TrendingUp, title: 'Startup Scouting', description: 'Find & rank investment opportunities', color: 'from-indigo-400 to-blue-500', path: '/for-investors/startup-scouting' },
    { icon: Send, title: 'Startup Matchmaking', description: 'AI-powered investor-startup matching', color: 'from-pink-400 to-rose-500', path: '/for-investors/startup-matchmaking' },
    { icon: DollarSign, title: 'Fundraising Assistance', description: 'Capital infusion opportunities', color: 'from-emerald-400 to-green-500', path: '/for-investors/fundraising-assistance' },
    { icon: Briefcase, title: 'Strategic Acquisition', description: 'High-value M&A targets', color: 'from-amber-400 to-yellow-500', path: '/for-investors/strategic-acquisition' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col group" data-testid="header-logo">
            <div className="flex items-center gap-3">
              <Logo />
              <div>
                <div className="text-xl font-bold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent leading-tight">
                  FounderScale
                </div>
                <div className="text-xs font-bold text-emerald-600 leading-tight tracking-wider">
                  CAPITAL
                </div>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 font-medium mt-1 ml-[60px] tracking-wide italic">
              From Pitch to Capital
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => link.hasMega && setMegaMenuOpen(link.hasMega)}
                onMouseLeave={() => setMegaMenuOpen(null)}
              >
                <Link
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`font-semibold transition-colors duration-200 relative group flex items-center gap-1 text-sm ${
                    isActive(link.path) ? 'text-emerald-600' : 'text-gray-700 hover:text-emerald-600'
                  }`}
                >
                  {link.name}
                  {link.hasMega && <ChevronDown className="h-4 w-4" />}
                  <span className={`absolute bottom-[-8px] left-0 h-0.5 bg-gradient-to-r from-emerald-600 to-green-600 transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>

                {/* Startup Mega Menu */}
                {link.hasMega === 'startups' && megaMenuOpen === 'startups' && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[900px] bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-3 gap-0">
                      <div className="col-span-2 p-8 bg-gradient-to-br from-emerald-50/50 to-white">
                        <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-6">Services</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {startupServices.map((service, idx) => (
                            <Link key={idx} to={service.path} data-testid={`mega-service-${idx}`}
                              className="flex items-start gap-3 p-3 rounded-xl hover:bg-emerald-50 transition-all duration-200 group/item">
                              <div className={`w-10 h-10 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm group-hover/item:scale-110 transition-transform duration-200`}>
                                <service.icon className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 text-sm mb-1 group-hover/item:text-emerald-600 transition-colors">{service.title}</h4>
                                <p className="text-xs text-gray-500 leading-snug">{service.description}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div className="p-8 bg-gradient-to-br from-gray-50 to-white border-l border-gray-100">
                        <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-6">By Funding Stage</h3>
                        <div className="space-y-3">
                          <Link to="/stage/early" className="block px-4 py-3 rounded-xl border-2 border-emerald-100 hover:border-emerald-400 hover:bg-emerald-50 transition-all duration-200 group/stage">
                            <div className="font-bold text-gray-900 text-sm group-hover/stage:text-emerald-600">Early Stage</div>
                            <div className="text-xs text-gray-500 mt-1">Ideation, Pre-Seed, Seed</div>
                          </Link>
                          <Link to="/stage/growth" className="block px-4 py-3 rounded-xl border-2 border-emerald-100 hover:border-emerald-400 hover:bg-emerald-50 transition-all duration-200 group/stage">
                            <div className="font-bold text-gray-900 text-sm group-hover/stage:text-emerald-600">Growth Stage</div>
                            <div className="text-xs text-gray-500 mt-1">Series A & B</div>
                          </Link>
                          <Link to="/stage/late" className="block px-4 py-3 rounded-xl border-2 border-emerald-100 hover:border-emerald-400 hover:bg-emerald-50 transition-all duration-200 group/stage">
                            <div className="font-bold text-gray-900 text-sm group-hover/stage:text-emerald-600">Late Stage</div>
                            <div className="text-xs text-gray-500 mt-1">Series C+ & Pre-IPO</div>
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-8 py-4 flex items-center justify-between">
                      <div>
                        <div className="text-white font-bold">Ready to raise capital?</div>
                        <div className="text-green-100 text-sm">Get a free consultation today</div>
                      </div>
                      <Link to="/contact">
                        <Button className="bg-white text-emerald-700 hover:bg-green-50 font-bold" data-testid="mega-cta-btn">
                          Connect With Us<ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}

                {/* Investor Mega Menu */}
                {link.hasMega === 'investors' && megaMenuOpen === 'investors' && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[900px] bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-3 gap-0">
                      <div className="col-span-2 p-8 bg-gradient-to-br from-emerald-50/50 to-white">
                        <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-6">Services</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {investorServices.map((service, idx) => (
                            <Link key={idx} to={service.path}
                              className="flex items-start gap-3 p-3 rounded-xl hover:bg-emerald-50 transition-all duration-200 group/item">
                              <div className={`w-10 h-10 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm group-hover/item:scale-110 transition-transform duration-200`}>
                                <service.icon className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 text-sm mb-1 group-hover/item:text-emerald-600 transition-colors">{service.title}</h4>
                                <p className="text-xs text-gray-500 leading-snug">{service.description}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div className="p-8 bg-gradient-to-br from-gray-50 to-white border-l border-gray-100">
                        <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-6">Investor Type</h3>
                        <div className="space-y-2">
                          <Link to="/investor-type/hni-angel" className="block px-3 py-2 rounded-lg hover:bg-emerald-50 transition-all text-sm font-semibold text-gray-700 hover:text-emerald-600">HNI & Angel</Link>
                          <Link to="/investor-type/micro-vcs" className="block px-3 py-2 rounded-lg hover:bg-emerald-50 transition-all text-sm font-semibold text-gray-700 hover:text-emerald-600">Micro VCs/VCs</Link>
                          <Link to="/investor-type/family-offices" className="block px-3 py-2 rounded-lg hover:bg-emerald-50 transition-all text-sm font-semibold text-gray-700 hover:text-emerald-600">Family Offices</Link>
                          <Link to="/investor-type/private-equity" className="block px-3 py-2 rounded-lg hover:bg-emerald-50 transition-all text-sm font-semibold text-gray-700 hover:text-emerald-600">Private Equity</Link>
                          <Link to="/investor-type/corporate" className="block px-3 py-2 rounded-lg hover:bg-emerald-50 transition-all text-sm font-semibold text-gray-700 hover:text-emerald-600">Corporate Investors</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
        <div className="hidden xl:block">
  <Button
  data-testid="header-cta-btn"
  onClick={() => window.location.assign('/contact')}
  className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
>
  Connect With Us
</Button>
</div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors">
            {isMenuOpen ? <X className="h-6 w-6 text-gray-700" /> : <Menu className="h-6 w-6 text-gray-700" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="xl:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)}
                  className={`font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ${
                    isActive(link.path) ? 'text-emerald-600 bg-emerald-50' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  {link.name}
                </Link>
              ))}
              <Button onClick={() => { setIsMenuOpen(false); window.location.href = '/contact'; }}
                className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold w-full mt-2">
                Connect With Us
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
